import { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { X, UserPlus, Loader2 } from 'lucide-react';
import type { RootState, AppDispatch } from '@/store';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { useNotification } from '@/hooks/useNotification';
import { updateUserAccess, removeInvitedUser, addNewUser, addGuestUser } from '../../../apiServices/userApi';
import { removeUser, addUser, addInvitedUser, removeInvitedUser as removeInvitedUserFromStore } from '../../../app/features/project/projectSlice';
import { validateEmailFormat } from '../../../util/UtilFunctions';

// ── Types ────────────────────────────────────────────────────────────

interface ProjectUser {
  uid: string;
  given_name: string;
  family_name: string;
  email: string;
  role: string;
  img?: string;
}

interface InvitedUser {
  uid?: string;
  email: string;
  role: string;
  status?: string;
  group?: string;
  inviter?: string;
  expires?: string;
  created?: string;
}

interface WorkspaceUser {
  uid: string;
  given_name: string;
  family_name: string;
  email: string;
  role: string;
  img?: string;
}

interface ProjectParticipiantsModalProps {
  closeModal: () => void;
  projectId: string;
  userRole: string;
}

type RoleId = 'pro' | 'user' | 'viewer' | 'guest';

const ROLE_OPTIONS: { id: RoleId; label: string }[] = [
  { id: 'pro', label: 'Pro' },
  { id: 'user', label: 'User' },
  { id: 'viewer', label: 'Viewer' },
  { id: 'guest', label: 'Guest' },
];

function getAvailableRoles(currentRole: string): { id: RoleId; label: string }[] {
  if (currentRole === 'admin') return ROLE_OPTIONS;
  if (currentRole === 'pro') return ROLE_OPTIONS.filter((r) => r.id !== 'pro');
  if (currentRole === 'user') return ROLE_OPTIONS.filter((r) => r.id === 'viewer');
  return [];
}

function roleBadgeVariant(role: string) {
  if (role === 'admin' || role === 'pro') return 'default' as const;
  if (role === 'guest') return 'secondary' as const;
  return 'outline' as const;
}

// ── Main Component ───────────────────────────────────────────────────

export default function ProjectParticipiantsModal({
  closeModal,
  projectId,
  userRole,
}: ProjectParticipiantsModalProps) {
  const dispatch = useDispatch<AppDispatch>();
  const { notify } = useNotification();

  const projectUsers = useSelector((state: RootState) => state.project.users) as ProjectUser[];
  const invitedUsers = useSelector((state: RootState) => state.project.invitedUsers) as InvitedUser[] | undefined;
  const workspaceUsers = useSelector((state: RootState) => state.workspace.workspaceUsers) as WorkspaceUser[];
  const currentUserGroup = useSelector((state: RootState) => state.workspace.currentUserGroup) as string;
  const currentUserDetails = useSelector((state: RootState) => state.user.oldUserDetails) as { role?: { S: string } } | null;
  const currentUserRole = currentUserDetails?.role?.S || userRole;

  const isAdminOrPro = currentUserRole === 'admin' || currentUserRole === 'pro';

  const availableWorkspaceUsers = useMemo(
    () => workspaceUsers?.filter((wu) => !projectUsers?.find((pu) => pu.uid === wu.uid)) || [],
    [workspaceUsers, projectUsers],
  );

  const sortedProjectUsers = useMemo(
    () => [...(projectUsers || [])].sort((a, b) => a.given_name.localeCompare(b.given_name)),
    [projectUsers],
  );

  return (
    <div className="space-y-5">
      {/* ── Current participants ── */}
      <section>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">People</h3>
        <ul className="space-y-1">
          {sortedProjectUsers.map((person) => (
            <PersonRow
              key={person.uid}
              person={person}
              projectId={projectId}
              canRemove={isAdminOrPro && person.role !== 'admin' && person.role !== 'pro'}
            />
          ))}
        </ul>
      </section>

      {/* ── Pending invitations ── */}
      {invitedUsers && invitedUsers.length > 0 && (
        <section>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Pending invitations</h3>
          <ul className="space-y-1">
            {invitedUsers.map((person) => (
              <li key={person.uid || person.email} className="flex items-center justify-between rounded-md px-2 py-1.5">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-accent-foreground">{person.email}</span>
                  <Badge variant={roleBadgeVariant(person.role)}>{person.role}</Badge>
                  {person.status === 'expired' && (
                    <span className="text-xs text-destructive">Expired</span>
                  )}
                </div>
                {isAdminOrPro && person.uid && (
                  <RevokeButton uid={person.uid} />
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* ── Add existing workspace users ── */}
      {isAdminOrPro && availableWorkspaceUsers.length > 0 && (
        <section>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Add existing people</h3>
          <ul className="space-y-1">
            {availableWorkspaceUsers.map((person) => (
              <AddWorkspaceUserRow key={person.uid} person={person} projectId={projectId} />
            ))}
          </ul>
        </section>
      )}

      {/* ── Invite new person ── */}
      {isAdminOrPro && (
        <section>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Add new people</h3>
          <InviteForm
            projectId={projectId}
            currentUserGroup={currentUserGroup}
            currentUserRole={currentUserRole}
          />
        </section>
      )}
    </div>
  );
}

// ── Sub-components ───────────────────────────────────────────────────

function PersonRow({
  person,
  projectId,
  canRemove,
}: {
  person: ProjectUser;
  projectId: string;
  canRemove: boolean;
}) {
  const [removing, setRemoving] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const { notify } = useNotification();

  const handleRemove = async () => {
    setRemoving(true);
    try {
      await updateUserAccess(person.uid, projectId, 0);
      dispatch(removeUser(person.uid));
      notify({ type: 'success', message: `${person.given_name} ${person.family_name} removed from project` });
    } catch {
      notify({ type: 'error', message: 'Could not remove user from project' });
      setRemoving(false);
    }
  };

  return (
    <li className="flex items-center gap-3 rounded-md px-2 py-1.5">
      <Avatar src={person.img} name={`${person.given_name} ${person.family_name}`} size="sm" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium text-accent-foreground">
            {person.given_name} {person.family_name}
          </span>
          <Badge variant={roleBadgeVariant(person.role)}>{person.role}</Badge>
        </div>
        <p className="truncate text-xs text-muted-foreground">{person.email}</p>
      </div>
      {canRemove && (
        <button
          onClick={handleRemove}
          disabled={removing}
          className="rounded p-1 text-muted-foreground hover:bg-secondary hover:text-muted-foreground disabled:opacity-50"
          title="Remove from project"
        >
          {removing ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
        </button>
      )}
    </li>
  );
}

function RevokeButton({ uid }: { uid: string }) {
  const [revoking, setRevoking] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const { notify } = useNotification();

  const handleRevoke = async () => {
    setRevoking(true);
    try {
      await removeInvitedUser(uid);
      dispatch(removeInvitedUserFromStore(uid));
      notify({ type: 'success', message: 'Invitation revoked' });
    } catch {
      notify({ type: 'error', message: 'Could not revoke invitation' });
      setRevoking(false);
    }
  };

  return (
    <button
      onClick={handleRevoke}
      disabled={revoking}
      className="rounded p-1 text-muted-foreground hover:bg-secondary hover:text-muted-foreground disabled:opacity-50"
      title="Revoke invitation"
    >
      {revoking ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
    </button>
  );
}

function AddWorkspaceUserRow({
  person,
  projectId,
}: {
  person: WorkspaceUser;
  projectId: string;
}) {
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const { notify } = useNotification();

  const handleAdd = async () => {
    setAdding(true);
    try {
      await updateUserAccess(person.uid, projectId, 1);
      dispatch(addUser(person));
      setAdded(true);
      notify({ type: 'success', message: `${person.given_name} ${person.family_name} added to project` });
    } catch {
      notify({ type: 'error', message: `Could not add ${person.given_name} to project` });
      setAdding(false);
    }
  };

  if (added) return null;

  return (
    <li className="flex items-center gap-3 rounded-md px-2 py-1.5">
      <Avatar src={person.img} name={`${person.given_name} ${person.family_name}`} size="sm" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium text-accent-foreground">
            {person.given_name} {person.family_name}
          </span>
          <Badge variant={roleBadgeVariant(person.role)}>{person.role}</Badge>
        </div>
        <p className="truncate text-xs text-muted-foreground">{person.email}</p>
      </div>
      <Button size="sm" onClick={handleAdd} disabled={adding}>
        {adding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserPlus className="mr-1 h-3.5 w-3.5" />}
        Add
      </Button>
    </li>
  );
}

function InviteForm({
  projectId,
  currentUserGroup,
  currentUserRole,
}: {
  projectId: string;
  currentUserGroup: string;
  currentUserRole: string;
}) {
  const [email, setEmail] = useState('');
  const [selectedRole, setSelectedRole] = useState<RoleId>('viewer');
  const [submitting, setSubmitting] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const { notify } = useNotification();

  const availableRoles = getAvailableRoles(currentUserRole);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    const trimmed = email.trim().toLowerCase();
    if (!trimmed) {
      notify({ type: 'info', message: 'Please enter an email address' });
      return;
    }
    if (trimmed.length > 50) {
      notify({ type: 'info', message: 'Email must be under 50 characters' });
      return;
    }
    if (!validateEmailFormat(trimmed)) {
      notify({ type: 'info', message: 'Invalid email format' });
      return;
    }

    setSubmitting(true);
    try {
      const response = selectedRole === 'guest'
        ? await addGuestUser(trimmed, projectId)
        : await addNewUser(trimmed, selectedRole, currentUserGroup, projectId);

      if (response.response?.includes?.('ERROR')) {
        throw new Error('Server returned error');
      }

      dispatch(addInvitedUser({
        uid: undefined,
        created: new Date().toString(),
        role: selectedRole,
        group: currentUserGroup,
        inviter: '',
        expires: '',
        email: trimmed,
      }));

      notify({ type: 'success', message: `Invited ${trimmed} as ${selectedRole}` });
      setEmail('');
    } catch {
      notify({ type: 'error', message: 'Could not invite user' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <Input
        type="email"
        placeholder="Email address"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <div className="flex flex-wrap gap-2">
        {availableRoles.map((role) => (
          <button
            key={role.id}
            type="button"
            onClick={() => setSelectedRole(role.id)}
            className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
              selectedRole === role.id
                ? 'border-primary bg-primary-muted text-primary-hover'
                : 'border-border text-muted-foreground hover:border-border'
            }`}
          >
            {role.label}
          </button>
        ))}
      </div>
      <Button type="submit" size="sm" disabled={submitting}>
        {submitting ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <UserPlus className="mr-1 h-3.5 w-3.5" />}
        Add
      </Button>
    </form>
  );
}
